import type { ReactNode } from "react";

type Tone = {
  bg: string;
  wash: string;
  wash2: string;
  soft: string;
  mid: string;
  strong: string;
  ink: string;
};

const program: Tone = {
  bg: "#ecfdf5",
  wash: "#a7f3d0",
  wash2: "#99f6e4",
  soft: "#d1fae5",
  mid: "#34d399",
  strong: "#059669",
  ink: "#0f766e",
};

const marketing: Tone = {
  bg: "#f5f3ff",
  wash: "#ddd6fe",
  wash2: "#e9d5ff",
  soft: "#ede9fe",
  mid: "#a78bfa",
  strong: "#7c3aed",
  ink: "#5b21b6",
};

const finance: Tone = {
  bg: "#fffbeb",
  wash: "#fde68a",
  wash2: "#fcd34d",
  soft: "#fef3c7",
  mid: "#fbbf24",
  strong: "#d97706",
  ink: "#b45309",
};

const compliance: Tone = {
  bg: "#eef2ff",
  wash: "#c7d2fe",
  wash2: "#ddd6fe",
  soft: "#e0e7ff",
  mid: "#818cf8",
  strong: "#4f46e5",
  ink: "#312e81",
};

function Art({ tone, children }: { tone: Tone; children: ReactNode }) {
  return (
    <svg viewBox="0 0 320 148" className="h-full w-full" aria-hidden="true">
      <rect width="320" height="148" fill={tone.bg} />
      <circle cx="286" cy="22" r="46" fill={tone.wash} />
      <circle cx="24" cy="132" r="32" fill={tone.wash2} />
      {children}
    </svg>
  );
}

export function ProgramPerformanceIllustration() {
  return (
    <Art tone={program}>
      <rect x="36" y="28" width="78" height="44" rx="12" fill="#ffffff" />
      <rect x="48" y="40" width="36" height="6" rx="3" fill={program.soft} />
      <rect x="48" y="52" width="48" height="8" rx="4" fill={program.strong} />
      <rect x="122" y="28" width="78" height="44" rx="12" fill="#ffffff" />
      <rect x="134" y="40" width="36" height="6" rx="3" fill={program.soft} />
      <rect x="134" y="52" width="40" height="8" rx="4" fill={program.mid} />
      <rect x="208" y="28" width="78" height="44" rx="12" fill="#ffffff" />
      <rect x="220" y="40" width="36" height="6" rx="3" fill={program.soft} />
      <rect x="220" y="52" width="28" height="8" rx="4" fill={program.ink} />
      <rect x="36" y="82" width="250" height="42" rx="12" fill="#ffffff" />
      <path
        d="M52 110c28-18 40-6 62-16 22-10 34 4 56-8 18-10 36-8 52-18"
        fill="none"
        stroke={program.strong}
        strokeWidth="3"
        strokeLinecap="round"
      />
      <circle cx="270" cy="68" r="5" fill={program.ink} />
    </Art>
  );
}

export function MemberActivityIllustration() {
  return (
    <Art tone={program}>
      <rect x="48" y="24" width="224" height="104" rx="16" fill="#ffffff" />
      {[0, 1, 2].map((row) => (
        <g key={row} transform={`translate(0 ${row * 30})`}>
          <circle cx="76" cy="48" r="10" fill={row === 0 ? program.strong : program.mid} />
          <rect x="96" y="42" width="70" height="6" rx="3" fill={program.soft} />
          <rect x="96" y="52" width="42" height="4" rx="2" fill={program.wash} />
          <rect x="188" y="44" width="64" height="8" rx="4" fill={program.soft} />
          <rect
            x="188"
            y="44"
            width={48 - row * 12}
            height="8"
            rx="4"
            fill={program.strong}
          />
        </g>
      ))}
    </Art>
  );
}

export function TierMovementIllustration() {
  return (
    <Art tone={program}>
      <rect x="58" y="86" width="64" height="36" rx="8" fill={program.wash} />
      <rect x="128" y="62" width="64" height="60" rx="8" fill={program.mid} />
      <rect x="198" y="36" width="64" height="86" rx="8" fill={program.strong} />
      <path
        d="M78 70l28-22 22 12 48-34"
        fill="none"
        stroke={program.ink}
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M164 22l12 4-8 10" fill={program.ink} />
    </Art>
  );
}

export function CampaignIllustration() {
  return (
    <Art tone={marketing}>
      <rect x="40" y="36" width="120" height="80" rx="14" fill="#ffffff" />
      <path d="M40 50l60 28 60-28" fill="none" stroke={marketing.strong} strokeWidth="3" />
      <rect x="40" y="36" width="120" height="22" rx="14" fill={marketing.mid} />
      <rect x="40" y="48" width="120" height="10" fill={marketing.mid} />
      <rect x="184" y="40" width="18" height="64" rx="6" fill={marketing.wash} />
      <rect x="210" y="28" width="18" height="76" rx="6" fill={marketing.mid} />
      <rect x="236" y="52" width="18" height="52" rx="6" fill={marketing.strong} />
      <rect x="262" y="64" width="18" height="40" rx="6" fill={marketing.ink} />
    </Art>
  );
}

export function EnrolmentIllustration() {
  return (
    <Art tone={marketing}>
      <circle cx="108" cy="62" r="22" fill="#ffffff" />
      <circle cx="108" cy="54" r="8" fill={marketing.strong} />
      <path d="M90 80c4-10 12-14 18-14s14 4 18 14" fill={marketing.mid} />
      <circle cx="156" cy="48" r="14" fill={marketing.strong} />
      <path d="M156 41v14M149 48h14" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" />
      <rect x="186" y="36" width="90" height="22" rx="11" fill="#ffffff" />
      <rect x="198" y="44" width="52" height="6" rx="3" fill={marketing.soft} />
      <rect x="186" y="66" width="90" height="22" rx="11" fill="#ffffff" />
      <rect x="198" y="74" width="40" height="6" rx="3" fill={marketing.wash} />
      <rect x="186" y="96" width="90" height="22" rx="11" fill="#ffffff" />
      <rect x="198" y="104" width="64" height="6" rx="3" fill={marketing.mid} />
    </Art>
  );
}

export function SegmentSnapshotIllustration() {
  return (
    <Art tone={marketing}>
      <circle cx="118" cy="78" r="46" fill="#ffffff" />
      <path d="M118 78 L118 32 A46 46 0 0 1 158 98 Z" fill={marketing.strong} />
      <path d="M118 78 L158 98 A46 46 0 0 1 86 108 Z" fill={marketing.mid} />
      <path d="M118 78 L86 108 A46 46 0 0 1 118 32 Z" fill={marketing.wash} />
      <circle cx="118" cy="78" r="18" fill="#ffffff" />
      <rect x="188" y="42" width="88" height="16" rx="8" fill="#ffffff" />
      <circle cx="202" cy="50" r="4" fill={marketing.strong} />
      <rect x="188" y="66" width="72" height="16" rx="8" fill="#ffffff" />
      <circle cx="202" cy="74" r="4" fill={marketing.mid} />
      <rect x="188" y="90" width="80" height="16" rx="8" fill="#ffffff" />
      <circle cx="202" cy="98" r="4" fill={marketing.wash2} />
    </Art>
  );
}

export function SegmentMembersIllustration() {
  return (
    <Art tone={marketing}>
      <rect x="52" y="26" width="216" height="100" rx="14" fill="#ffffff" />
      <rect x="52" y="26" width="216" height="24" rx="14" fill={marketing.strong} />
      <rect x="52" y="40" width="216" height="10" fill={marketing.strong} />
      {[0, 1, 2].map((row) => (
        <g key={row} transform={`translate(0 ${row * 22})`}>
          <rect x="68" y="60" width="46" height="8" rx="4" fill={marketing.soft} />
          <rect x="126" y="60" width="70" height="8" rx="4" fill={marketing.wash} />
          <rect x="208" y="60" width="40" height="8" rx="4" fill={marketing.mid} />
        </g>
      ))}
    </Art>
  );
}

export function LiabilityIllustration() {
  return (
    <Art tone={finance}>
      <rect x="40" y="30" width="112" height="92" rx="14" fill="#ffffff" />
      <rect x="54" y="46" width="64" height="6" rx="3" fill={finance.soft} />
      <rect x="54" y="60" width="84" height="8" rx="4" fill={finance.wash} />
      <rect x="54" y="76" width="72" height="8" rx="4" fill={finance.mid} />
      <rect x="54" y="92" width="56" height="8" rx="4" fill={finance.strong} />
      <rect x="168" y="30" width="112" height="92" rx="14" fill="#ffffff" />
      <rect x="182" y="46" width="64" height="6" rx="3" fill={finance.soft} />
      <rect x="182" y="64" width="40" height="36" rx="8" fill={finance.soft} />
      <rect x="230" y="52" width="40" height="48" rx="8" fill={finance.strong} />
    </Art>
  );
}

export function IssuedBySourceIllustration() {
  return (
    <Art tone={finance}>
      <rect x="44" y="28" width="232" height="96" rx="14" fill="#ffffff" />
      {(
        [
          [168, finance.strong],
          [132, finance.mid],
          [104, finance.wash2],
          [78, finance.ink],
        ] as const
      ).map(([width, fill], index) => (
        <rect
          key={index}
          x="64"
          y={44 + index * 18}
          width={width}
          height="10"
          rx="5"
          fill={fill}
        />
      ))}
    </Art>
  );
}

export function BreakageIllustration() {
  return (
    <Art tone={finance}>
      <rect x="48" y="36" width="70" height="80" rx="12" fill="#ffffff" />
      <rect x="60" y="72" width="14" height="28" rx="4" fill={finance.strong} />
      <rect x="80" y="60" width="14" height="40" rx="4" fill={finance.mid} />
      <rect x="100" y="84" width="14" height="16" rx="4" fill={finance.wash2} />
      <rect x="132" y="36" width="140" height="80" rx="12" fill="#ffffff" />
      <path
        d="M150 96c18-8 28-28 46-24 16 4 22-10 40-22"
        fill="none"
        stroke={finance.ink}
        strokeWidth="3"
        strokeLinecap="round"
        strokeDasharray="5 5"
      />
      <path
        d="M150 88c20-4 30-10 48-8 16 2 24 8 42 2"
        fill="none"
        stroke={finance.strong}
        strokeWidth="3"
        strokeLinecap="round"
      />
    </Art>
  );
}

export function RedemptionIllustration() {
  return (
    <Art tone={finance}>
      <rect x="78" y="24" width="164" height="104" rx="16" fill="#ffffff" />
      <path
        d="M78 48h164"
        stroke={finance.wash}
        strokeWidth="2"
        strokeDasharray="6 6"
      />
      <circle cx="78" cy="48" r="10" fill={finance.bg} />
      <circle cx="242" cy="48" r="10" fill={finance.bg} />
      <rect x="100" y="64" width="90" height="8" rx="4" fill={finance.soft} />
      <rect x="100" y="80" width="70" height="8" rx="4" fill={finance.wash} />
      <rect x="100" y="96" width="48" height="12" rx="6" fill={finance.strong} />
      <circle cx="214" cy="100" r="14" fill={finance.mid} />
      <path d="M208 100h12M214 94v12" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" />
    </Art>
  );
}

export function RuleCostIllustration() {
  return (
    <Art tone={finance}>
      <path
        d="M70 40h110l28 34-28 34H70a16 16 0 0 1-16-16V56a16 16 0 0 1 16-16z"
        fill="#ffffff"
      />
      <circle cx="96" cy="74" r="8" fill={finance.strong} />
      <rect x="116" y="64" width="62" height="8" rx="4" fill={finance.soft} />
      <rect x="116" y="78" width="40" height="6" rx="3" fill={finance.wash} />
      <circle cx="230" cy="58" r="22" fill={finance.mid} />
      <circle cx="252" cy="92" r="16" fill={finance.strong} />
      <circle cx="214" cy="100" r="12" fill={finance.wash2} />
    </Art>
  );
}

export function LedgerIllustration() {
  return (
    <Art tone={compliance}>
      <rect x="86" y="22" width="28" height="108" rx="6" fill={compliance.wash} />
      <rect x="102" y="22" width="150" height="108" rx="14" fill="#ffffff" />
      <rect x="118" y="40" width="70" height="8" rx="4" fill={compliance.soft} />
      {[0, 1, 2, 3].map((row) => (
        <g key={row} transform={`translate(0 ${row * 16})`}>
          <circle cx="126" cy="66" r="4" fill={row % 2 === 0 ? compliance.strong : compliance.mid} />
          <rect x="138" y="62" width={88 - row * 8} height="6" rx="3" fill={compliance.soft} />
        </g>
      ))}
    </Art>
  );
}
